import { ComponentType } from 'react';
import { colors } from 'config/themes/colors';
import styled, { css } from 'styled-components';
import { palette } from 'styled-theme';
import { transition } from '../../../config/style-util';

export interface SelectStyleProps {
  joinright?: string;
  'data-rtl'?: string;
}

export const BasicSelectHoverStyles = css`
  &:hover {
    border-color: ${colors.blue[600]};
  }
`;

export const BasicSelectFocusedStyles = css`
  border-color: ${colors.blue[600]};
  box-shadow: 0 0 0 3px
    ${({ theme }) => (theme.name === 'atsFancy' ? colors.blue[100] : colors.infoBlueDark[700])};
  outline: 0;
`;

const StyledSelect = <P extends object>(component: ComponentType<P>) => styled(component)`
  &.ant-select {
    /* height: 40px; */
    .ant-select-content-has-value {
      font-weight: 400;
    }
    border-radius: ${palette('borderRadius', 15)};
    &.ant-select-disabled {
      background: ${palette('background', 59)};
      color: ${palette('foreground', 0)};
    }

    .ant-select-placeholder {
      color: ${({ theme }) =>
        theme.name == 'atsFancy' ? palette('grayscale', 0) : theme.text.fadedGray};
    }
  }
  background: ${palette('background', 59)};
  color: ${palette('foreground', 20)};
  border: 1px solid ${palette('borderColor', 5)};
  border-radius: ${palette('borderRadius', 15)};
  ${BasicSelectHoverStyles}

  &.ant-select-focused:not(.ant-select-disabled) {
    ${BasicSelectFocusedStyles}
  }

  &.ant-select-multiple .ant-select-content .ant-select-selection-item {
    background-color: ${({ theme }) => theme.select.tag.background.default};
    color: ${palette('foreground', 0)};
    .ant-select-selection-item-content {
      font-weight: 400;
      font-size: 0.8rem;
    }
    .ant-select-selection-item-remove {
      background-color: ${({ theme }) => theme.select.tag.background.default};
      color: ${({ theme }) => theme.select.tag.close.color.default};
      svg {
        color: ${({ theme }) => theme.select.tag.close.color.default};
        height: 10px;
        vertical-align: top;
      }
    }
  }
`;

const AntSelect = <P extends object>(component: ComponentType<P>) => styled(component)<SelectStyleProps>`
  &.ant-select {
    box-sizing: border-box;
    position: relative;
    color: ${palette('foreground', 0)};
    font-size: ${(props) => props.theme.input.fontSize};
    .ant-select-content-has-value {
      font-weight: 400;
    }

    &.ant-select-disabled {
      .ant-select-selector {
        background: ${palette('background', 59)};
        color: ${palette('foreground', 0)};
      }
    }

    .ant-select-placeholder {
      display: flex;
      align-items: center;
      color: ${({ theme }) =>
        theme.name == 'atsFancy' ? palette('grayscale', 0) : theme.text.fadedGray};
    }

    background-color: ${palette('background', 46)};
    border-radius: ${palette('borderRadius', 15)};
    border-top-right-radius: ${(props) => (props.joinright ? '0px' : palette('borderRadius', 15))};
    border-bottom-right-radius: ${(props) =>
      props.joinright ? '0px' : palette('borderRadius', 15)};
    border: 1px solid ${palette('border', 0)};
    ${transition()};

    /* Keep in sync with Select.controlHeight in src/config/themes/antdTheme.ts
       controlHeight drives antd internal layout (placeholder position, line-height, padding)
       while this sets the actual outer height — a mismatch breaks placeholder alignment */
    height: 40px;

    position: relative;
    cursor: pointer;
    > .ant-select-content {
      line-height: 2.7rem;
    }

    ${BasicSelectHoverStyles}

    .ant-select-suffix {
      right: ${(props) => (props['data-rtl'] === 'rtl' ? 'inherit' : '8px')};
      left: ${(props) => (props['data-rtl'] === 'rtl' ? '8px' : 'inherit')};
    }

    &.ant-select-focused:not(.ant-select-disabled) {
      ${BasicSelectFocusedStyles}
    }

    .ant-select-selection-item {
      border: none !important;
      background-color: ${palette('background', 67)};
    }

    &.ant-select-multiple .ant-select-content {
      height: 100%;
      align-content: center;
    }

    &.ant-select-multiple .ant-select-content .ant-select-selection-item {
      background-color: ${palette('background', 67)};
      color: ${palette('foreground', 0)};
      .ant-select-selection-item-remove {
        background-color: ${palette('background', 67)};
        svg {
          color: ${palette('foreground', 0)};
        }
      }
    }

    .ant-select-tree .ant-select-tree-treenode .ant-select-tree-node-content-wrapper {
      font-size: 13px;
      color: ${palette('text', 1)};
    }
  }
`;

export { AntSelect, StyledSelect };
